import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Menu, ChevronDown, User, LogOut, Check, CheckCheck, Shield } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore, isDevMode, type DevRole } from '../../stores/auth-store';
import { useUiStore } from '../../stores/ui-store';
import { useLogout } from '../../hooks/useAuth';
import { useNotifications, useMarkAsRead, useMarkAllAsRead } from '../../hooks/useNotifications';

import type { NotificationDTO } from '../../hooks/notification-types';

function formatRelativeTime(dateStr: string): string {
  const now = Date.now();
  const date = new Date(dateStr).getTime();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMin < 1) return 'hace un momento';
  if (diffMin < 60) return `hace ${diffMin} min`;
  if (diffHours < 24) return `hace ${diffHours}h`;
  if (diffDays < 7) return `hace ${diffDays}d`;
  return new Date(dateStr).toLocaleDateString('es-EC');
}

function getEntityRoute(notification: NotificationDTO): string | null {
  if (!notification.entity_type || !notification.entity_id) return null;

  switch (notification.entity_type) {
    case 'project':
    case 'projects':
      return `/proyectos/${notification.entity_id}`;
    case 'proforma':
    case 'proformas':
      return `/proformas/${notification.entity_id}`;
    case 'planilla':
    case 'planillas':
      return `/planillas/${notification.entity_id}`;
    case 'pedido':
    case 'pedidos':
      return `/despachos`;
    case 'anticipo':
    case 'anticipos':
      return `/proyectos/${notification.entity_id}`;
    case 'medida':
    case 'medidas':
      return `/proyectos/${notification.entity_id}?tab=Medidas`;
    default:
      return null;
  }
}

const DEV_ROLE_LABELS: Record<DevRole, string> = {
  gerente: 'Gerente',
  operaciones: 'Operaciones',
  administrativo: 'Administrativo',
  residente: 'Residente',
  compras: 'Compras',
};

export function Header() {
  const user = useAuthStore((s) => s.user);
  const devRole = useAuthStore((s) => s.devRole);
  const setDevRole = useAuthStore((s) => s.setDevRole);
  const logoutMutation = useLogout();
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [devRoleOpen, setDevRoleOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);
  const devRoleRef = useRef<HTMLDivElement>(null);

  const { data: notifData } = useNotifications(!!user);
  const markRead = useMarkAsRead();
  const markAllRead = useMarkAllAsRead();

  const unreadCount = notifData?.unread_count ?? 0;
  const notifications = notifData?.data ?? [];

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
      if (devRoleRef.current && !devRoleRef.current.contains(e.target as Node)) {
        setDevRoleOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleNotifClick = (notification: NotificationDTO) => {
    if (!notification.is_read) {
      markRead.mutate(notification.id);
    }
    const route = getEntityRoute(notification);
    if (route) {
      navigate(route);
    }
    setNotifOpen(false);
  };

  return (
    <header className="relative z-20 flex h-14 items-center justify-between border-b border-white/20 bg-white/40 backdrop-blur-sm px-4 lg:px-6">
      {/* Left: hamburger (mobile) */}
      <button
        className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 lg:hidden"
        onClick={toggleSidebar}
        aria-label="Abrir menú"
      >
        <Menu size={20} />
      </button>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Right: dev role + notifications + user */}
      <div className="flex items-center gap-3">
        {/* Dev role switcher (dev mode only) */}
        {isDevMode && (
          <div ref={devRoleRef} className="relative">
            <button
              className="flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100"
              onClick={() => setDevRoleOpen(!devRoleOpen)}
            >
              <Shield size={14} />
              <span className="hidden sm:inline">DEV:</span>
              {DEV_ROLE_LABELS[devRole]}
              <ChevronDown size={12} />
            </button>

            {devRoleOpen && (
              <div className="absolute right-0 top-full mt-1 w-44 rounded-lg border border-amber-200 bg-white py-1 shadow-lg z-50">
                {(Object.keys(DEV_ROLE_LABELS) as DevRole[]).map((role) => (
                  <button
                    key={role}
                    className={`flex w-full items-center gap-2 px-3 py-1.5 text-sm hover:bg-amber-50 ${
                      devRole === role ? 'bg-amber-100 font-medium text-amber-900' : 'text-gray-700'
                    }`}
                    onClick={() => {
                      setDevRole(role);
                      setDevRoleOpen(false);
                      queryClient.clear();
                      window.location.reload();
                    }}
                  >
                    {devRole === role && <Check size={14} className="text-amber-600" />}
                    {devRole !== role && <span className="w-3.5" />}
                    {DEV_ROLE_LABELS[role]}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Notification bell */}
        <div ref={notifRef} className="relative">
          <button
            className="relative rounded-lg p-2 text-gray-500 hover:bg-gray-100"
            aria-label="Notificaciones"
            onClick={() => setNotifOpen(!notifOpen)}
          >
            <Bell size={20} />
            {unreadCount > 0 && (
              <span className="absolute right-1 top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>

          {/* Notification dropdown */}
          {notifOpen && (
            <div className="fixed left-3 right-3 top-14 z-50 rounded-xl border border-white/30 bg-white/95 backdrop-blur-md shadow-lg sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-1 sm:w-96">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
                <h3 className="text-sm font-semibold text-gray-800">Notificaciones</h3>
                {unreadCount > 0 && (
                  <button
                    className="flex items-center gap-1 text-xs text-brand-blue hover:text-brand-blue/80"
                    onClick={() => markAllRead.mutate()}
                    disabled={markAllRead.isPending}
                  >
                    <CheckCheck size={14} />
                    Marcar todas como leídas
                  </button>
                )}
              </div>

              {/* Notification list */}
              <div className="max-h-80 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="px-4 py-8 text-center text-sm text-gray-400">
                    No hay notificaciones
                  </div>
                ) : (
                  notifications.map((notif) => (
                    <button
                      key={notif.id}
                      className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50 ${
                        notif.is_read ? 'opacity-60' : 'bg-blue-50/40'
                      }`}
                      onClick={() => handleNotifClick(notif)}
                    >
                      {/* Read indicator */}
                      <div className="mt-1.5 flex-shrink-0">
                        {notif.is_read ? (
                          <Check size={12} className="text-gray-300" />
                        ) : (
                          <div className="h-2 w-2 rounded-full bg-brand-blue" />
                        )}
                      </div>

                      {/* Content */}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-gray-800">
                          {notif.titulo}
                        </p>
                        <p className="mt-0.5 line-clamp-2 text-xs text-gray-500">
                          {notif.mensaje}
                        </p>
                        <p className="mt-1 text-[10px] text-gray-400">
                          {formatRelativeTime(notif.created_at)}
                        </p>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* User dropdown */}
        {user && (
          <div ref={dropdownRef} className="relative">
            <button
              className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-blue text-xs font-bold text-white">
                {user.nombre.charAt(0).toUpperCase()}
              </div>
              <span className="hidden md:inline font-medium truncate max-w-[120px]">
                {user.nombre}
              </span>
              <ChevronDown size={14} className="text-gray-400" />
            </button>

            {dropdownOpen && (
              <div className="absolute right-0 top-full mt-1 w-48 rounded-lg border border-white/30 bg-white/90 backdrop-blur-md py-1 shadow-lg">
                <button
                  className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  onClick={() => {
                    setDropdownOpen(false);
                    navigate('/perfil');
                  }}
                >
                  <User size={16} />
                  Perfil
                </button>
                <hr className="my-1 border-gray-200" />
                <button
                  className="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  disabled={logoutMutation.isPending}
                  onClick={() => {
                    setDropdownOpen(false);
                    logoutMutation.mutate();
                  }}
                >
                  <LogOut size={16} />
                  Cerrar Sesión
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
